import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Aggregate logs by operation, model, and time proximity (within 1 hour)
function aggregateLogs(rawLogs) {
  if (!rawLogs || rawLogs.length === 0) return [];

  const aggregated = [];
  const ONE_HOUR_MS = 60 * 60 * 1000;

  for (const log of rawLogs) {
    const logTime = new Date(log.created_at).getTime();

    // Try to find an existing group to merge with
    const existingGroup = aggregated.find(group => {
      const groupTime = new Date(group.created_at).getTime();
      const timeDiff = Math.abs(logTime - groupTime);

      return (
        group.operation === log.operation &&
        group.model === log.model &&
        timeDiff <= ONE_HOUR_MS
      );
    });

    if (existingGroup) {
      // Merge into existing group
      existingGroup.count += 1;
      existingGroup.credits_used += (log.credits_used || 0);
      existingGroup.input_token_count += (log.input_token_count || 0);
      existingGroup.output_token_count += (log.output_token_count || 0);
      existingGroup.ids.push(log.id);

      // Collect unique article IDs
      if (log.article_id && !existingGroup.article_ids.includes(log.article_id)) {
        existingGroup.article_ids.push(log.article_id);
      }

      // Update time to the most recent
      if (logTime > new Date(existingGroup.created_at).getTime()) {
        existingGroup.created_at = log.created_at;
      }
    } else {
      // Create new group
      aggregated.push({
        id: log.id,
        ids: [log.id],
        operation: log.operation,
        model: log.model,
        provider: log.provider,
        credits_used: log.credits_used || 0,
        input_token_count: log.input_token_count || 0,
        output_token_count: log.output_token_count || 0,
        created_at: log.created_at,
        count: 1,
        article_ids: log.article_id ? [log.article_id] : [],
      });
    }
  }

  // Sort by most recent first
  return aggregated.sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export function useCreditHistory() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 100; // Fetch more raw logs since we'll aggregate them

  async function fetchLogs(loadMore = false) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const currentOffset = loadMore ? offset : 0;

      const { data, error } = await supabase
        .from('reedle_credit_usage_logs')
        .select('id, operation, model, provider, credits_used, input_token_count, output_token_count, created_at, article_id')
        .eq('user_id', user.id)
        .gt('credits_used', 0) // exclude 0-cost analytics rows (e.g. free-tier PDF processing)
        .order('created_at', { ascending: false })
        .range(currentOffset, currentOffset + LIMIT - 1);

      if (error) {
        console.error('Error fetching credit history:', error);
        setLoading(false);
        return;
      }

      if (loadMore) {
        // Aggregate all logs together
        const allRawLogs = [...logs.flatMap(l => l.ids.map(id => ({
          id,
          operation: l.operation,
          model: l.model,
          provider: l.provider,
          credits_used: l.credits_used / l.count,
          input_token_count: l.input_token_count / l.count,
          output_token_count: l.output_token_count / l.count,
          created_at: l.created_at
        }))), ...(data || [])];
        setLogs(aggregateLogs(allRawLogs));
      } else {
        setLogs(aggregateLogs(data || []));
      }

      // Check if there are more logs
      setHasMore(data && data.length === LIMIT);
      setOffset(currentOffset + LIMIT);
      setLoading(false);
    } catch (err) {
      console.error('Error in fetchLogs:', err);
      setLoading(false);
    }
  }

  async function loadMore() {
    await fetchLogs(true);
  }

  // After the declaration so the compiler lint sees fetchLogs as declared first.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetchLogs is async-first: every setState happens after an await, never synchronously in this effect
    fetchLogs();
  }, []);

  return { logs, loading, hasMore, loadMore, refresh: () => fetchLogs(false) };
}

